export var NotificationType;
(function (NotificationType) {
    // notifications to therapists
    /**
     * `patient.onboarded`: a new patient finished onboarding. Sent to the assigned
     * therapist if the patient already has one; otherwise to every ADMIN / MANAGEMENT
     * user of the patient's office.
     */
    NotificationType["PATIENT_REGISTER"] = "PATIENT_REGISTER";
    /**
     * `office-user.onboarded`: a new ADMIN or THERAPIST joined an office. Sent to the
     * existing ADMIN / MANAGEMENT users of that office (excluding the new user).
     * Named after the event — deliberately not `THERAPIST_REGISTER`, since ADMINs
     * trigger it too (#1381).
     */
    NotificationType["OFFICE_USER_REGISTER"] = "OFFICE_USER_REGISTER";
    /**
     * `patient.handover`: a patient was handed to a new therapist. Sent to the NEW
     * therapist now responsible for the patient.
     */
    NotificationType["PATIENT_HAND_OVER"] = "PATIENT_HAND_OVER";
    /**
     * @deprecated No longer dispatched (TM-8965). Was meant for the OLD therapist who
     * lost a patient on handover, but it belonged to no notification settings
     * group, so it could never reach the therapist on any channel and only wrote an
     * invisible row. Kept only in the DB enum and fixtures.
     */
    NotificationType["PATIENT_REMOVED"] = "PATIENT_REMOVED";
    /**
     * `appointment.participant-status-changed` (status ACCEPTED / DECLINED). Sent
     * to the appointment's therapist to acknowledge the patient's response. Fires
     * for any appointment — video or in-person — keyed off the appointment.
     */
    NotificationType["APPOINTMENT_ACCEPTED"] = "APPOINTMENT_ACCEPTED";
    /** @see {@link APPOINTMENT_ACCEPTED} — the DECLINED counterpart. */
    NotificationType["APPOINTMENT_DECLINED"] = "APPOINTMENT_DECLINED";
    /**
     * `appointment.participant-withdrew` (TM-9535): the patient called off a
     * booked appointment from their own appointment overview — an Absage, not an
     * answer to the invitation. Sent to the appointment's therapist, whether the
     * whole appointment ended or only the patient's place in a group appointment.
     */
    NotificationType["APPOINTMENT_CANCELLED_BY_PATIENT"] = "APPOINTMENT_CANCELLED_BY_PATIENT";
    /**
     * `video-call.handover`: an ongoing video call was handed to another
     * therapist. Sent to the NEW therapist taking over the call.
     */
    NotificationType["CALL_THERAPIST_HANDOVER_ACTIVE"] = "CALL_THERAPIST_HANDOVER_ACTIVE";
    /**
     * @deprecated No longer dispatched anywhere in the codebase (kept only in the
     * DB enum and fixtures). The handover flow now emits {@link CALL_THERAPIST_HANDOVER_ACTIVE}
     * to the new therapist and {@link CALL_THERAPIST_HANDOVER_PASSIVE_PATIENT} to
     * the patients.
     */
    NotificationType["CALL_THERAPIST_HANDOVER_PASSIVE_THERAPIST"] = "CALL_THERAPIST_HANDOVER_PASSIVE_THERAPIST";
    /**
     * @deprecated No longer dispatched (TM-8965). Was meant for office staff when a
     * colleague created a ONE_OFFICE exercise, but it belonged to no notification
     * settings group, so it could never reach staff on any channel and only wrote
     * invisible rows. Kept only in the DB enum and fixtures.
     */
    NotificationType["NEW_OFFICE_EXERCISE"] = "NEW_OFFICE_EXERCISE";
    /**
     * `exercise-program.expired`: a patient's exercise program reached its end
     * date. Sent to the patient's therapist.
     */
    NotificationType["PROGRAM_EXPIRED"] = "PROGRAM_EXPIRED";
    /**
     * `training-session.completed`: the patient finished a training session. Sent
     * to the therapist for normal results; {@link CRITICAL_PROGRAM_VALUES} is used
     * instead when `trainingSessionIsCritical` flags the session (e.g. high pain
     * values).
     */
    NotificationType["NEW_PROGRAM_STATS"] = "NEW_PROGRAM_STATS";
    /** @see {@link NEW_PROGRAM_STATS} — the critical-values counterpart. */
    NotificationType["CRITICAL_PROGRAM_VALUES"] = "CRITICAL_PROGRAM_VALUES";
    /**
     * `office-product-assignment.created`: a shop product was assigned to a
     * patient. Sent to the patient's therapist and the office location owner.
     */
    NotificationType["NEW_PRODUCT_ASSIGNMENT"] = "NEW_PRODUCT_ASSIGNMENT";
    /**
     * `office-product-assignment.warning`: a product assignment expires in 7 days.
     * Sent to the patient's therapist and the office location owner.
     */
    NotificationType["PRODUCT_ASSIGNMENT_7_DAY_WARNING"] = "PRODUCT_ASSIGNMENT_7_DAY_WARNING";
    // notifications to patients
    /**
     * `video-call.beginning-reminder`: the scheduled "call starts soon" reminder
     * (enqueued at invite time). Sent to the invited patient.
     */
    NotificationType["CALL_BEGINNING_REMINDER"] = "CALL_BEGINNING_REMINDER";
    /**
     * @deprecated No longer dispatched (TM-8965). Was the patient's own
     * confirmation that their accept/decline was recorded — an "uncritical own
     * action" belonging to no notification settings group, so it could never reach
     * the patient on any channel and only wrote an invisible row. Kept only in the
     * DB enum and fixtures.
     */
    NotificationType["APPOINTMENT_ACCEPT_SUCCESS"] = "APPOINTMENT_ACCEPT_SUCCESS";
    /**
     * @deprecated No longer dispatched (TM-8965).
     * @see {@link APPOINTMENT_ACCEPT_SUCCESS} — the DECLINE counterpart.
     */
    NotificationType["APPOINTMENT_DECLINE_SUCCESS"] = "APPOINTMENT_DECLINE_SUCCESS";
    /**
     * `appointment.patient-invited`: the patient was invited to an appointment.
     * Sent for every appointment — video or in-person — keyed off the appointment.
     */
    NotificationType["APPOINTMENT_INVITE"] = "APPOINTMENT_INVITE";
    /**
     * `video-call.canceled`: a video call was cancelled. Sent to every patient of
     * that call.
     */
    NotificationType["CALL_CANCELED"] = "CALL_CANCELED";
    /**
     * `patient.handover`: sent to the patient themselves when they are handed to a
     * new therapist.
     */
    NotificationType["PATIENT_HAND_OVER_AS_PATIENT"] = "PATIENT_HAND_OVER_AS_PATIENT";
    /**
     * `video-call.handover`: sent to every patient of a call that was handed to a
     * new therapist mid-call.
     */
    NotificationType["CALL_THERAPIST_HANDOVER_PASSIVE_PATIENT"] = "CALL_THERAPIST_HANDOVER_PASSIVE_PATIENT";
    /**
     * `exercise-program.created`: a new exercise program was assigned. Sent to the
     * patient the program belongs to.
     */
    NotificationType["NEW_EXERCISE_PROGRAM"] = "NEW_EXERCISE_PROGRAM";
    /**
     * `exercise-program.updated`: an existing program was changed. Sent to the
     * patient the program belongs to.
     */
    NotificationType["EXERCISE_PROGRAM_UPDATED"] = "EXERCISE_PROGRAM_UPDATED";
    /**
     * @deprecated No longer dispatched (TM-8965). Was the patient's own
     * confirmation that they completed a training session — an "uncritical own
     * action" belonging to no notification settings group, so it could never reach
     * the patient on any channel and only wrote an invisible row. Kept only in the
     * DB enum and fixtures.
     */
    NotificationType["COMPLETED_EXERCISE_PROGRAM"] = "COMPLETED_EXERCISE_PROGRAM";
    /**
     * `exercise-program.daily-reminder`: the daily "today's exercises" digest.
     * Sent to the patient at the hour they picked in
     * `UserSettings.trainingReminderTime` (TM-9530), and gated by the
     * "Trainingserinnerungen" settings category — see
     * {@link trainingReminderNotificationTypes}.
     */
    NotificationType["TODAYS_EXERCISE_SCHEDULE"] = "TODAYS_EXERCISE_SCHEDULE";
    /**
     * `training-session.completion-reminder`: nudge for a program not yet
     * completed. Sent to the patient.
     */
    NotificationType["EXERCISE_PROGRAM_COMPLETION_REMINDER"] = "EXERCISE_PROGRAM_COMPLETION_REMINDER";
    /**
     * `invoice.created` (Stripe/subscription billing): a new practice invoice is
     * available. Sent to the invoice's user, or the office location owner when the
     * invoice has no direct user. Practice-facing, distinct from the
     * `PATIENT_INVOICE_*` below.
     */
    NotificationType["NEW_INVOICE_AVAILABLE"] = "NEW_INVOICE_AVAILABLE";
    /**
     * `DeliverPatientInvoiceCommand`: a patient billing invoice is delivered.
     * ISSUED for normal delivery, {@link PATIENT_INVOICE_CANCELLED} when mode is
     * 'STORNO'. Sent to the patient. Both are part of `invoiceNotificationTypes`
     * (kill switch / opt-out).
     */
    NotificationType["PATIENT_INVOICE_ISSUED"] = "PATIENT_INVOICE_ISSUED";
    /** @see {@link PATIENT_INVOICE_ISSUED} — the STORNO counterpart. */
    NotificationType["PATIENT_INVOICE_CANCELLED"] = "PATIENT_INVOICE_CANCELLED";
    /**
     * `SendHonorarvereinbarungEmailCommand`: a staff member explicitly sends the
     * Honorarvereinbarung PDF to the patient (TM-9198, #986). Deliberately NOT
     * part of `invoiceNotificationTypes`: the agreement is a contract, not a tax
     * document, so it rides none of the invoice Zustellung suppression union (kill
     * switch / opt-out). Sending is a single explicit staff action.
     */
    NotificationType["HONORARVEREINBARUNG_SENT"] = "HONORARVEREINBARUNG_SENT";
    // appointment management
    /**
     * `appointment.rescheduled` → enqueued delayed job → `AppointmentRescheduledProcessor`:
     * an appointment's time (and possibly therapist) changed. Sent to every patient
     * participant once the debounce window elapses.
     */
    NotificationType["APPOINTMENT_RESCHEDULED"] = "APPOINTMENT_RESCHEDULED";
    /**
     * `appointment.booking-confirmed` (PRD #1343): the practice confirmed a
     * patient's pending-hold self-booking. Sent to the booking patient — their
     * "angefragt" appointment is now firm.
     */
    NotificationType["BOOKING_CONFIRMED"] = "BOOKING_CONFIRMED";
    /**
     * `appointment.booking-declined` (PRD #1343): the practice declined a
     * patient's pending-hold self-booking. Sent to the booking patient, carrying
     * the fixed `bookingDeclineReason` shown verbatim (no free text).
     */
    NotificationType["BOOKING_DECLINED"] = "BOOKING_DECLINED";
    /**
     * `appointment.booking-auto-cancelled` (PRD #1343, RS-003): the confirmation
     * sweep auto-cancelled a patient's pending hold that was still unconfirmed at
     * the cutoff before its start. Sent to the booking patient with a
     * contact-the-practice message (no decline reason — practice inaction).
     */
    NotificationType["BOOKING_AUTO_CANCELLED"] = "BOOKING_AUTO_CANCELLED";
    /**
     * `user.session-ended`: the user's Keycloak sessions were terminated. Sent to
     * that user.
     */
    NotificationType["SYSTEM_USER_SESSIONS_ENDED"] = "SYSTEM_USER_SESSIONS_ENDED";
    /**
     * `office-location.payed-feature-updated`: the office location's Stripe
     * entitlements changed. Sent to all allowed users and the office location
     * owner.
     */
    NotificationType["SYSTEM_USER_STRIPE_ENTITLEMENTS_UPDATED"] = "SYSTEM_USER_STRIPE_ENTITLEMENTS_UPDATED";
    /**
     * `connected-stripe-account.payout-failed`: a Stripe payout failed. Sent to
     * the main admin and every affected office location owner.
     */
    NotificationType["SYSTEM_USER_STRIPE_PAYOUT_FAILED"] = "SYSTEM_USER_STRIPE_PAYOUT_FAILED";
})(NotificationType || (NotificationType = {}));
export const invoiceNotificationTypes = [
    NotificationType.NEW_INVOICE_AVAILABLE,
    NotificationType.PATIENT_INVOICE_ISSUED,
    NotificationType.PATIENT_INVOICE_CANCELLED,
];
export const registrationNotificationTypes = [
    NotificationType.PATIENT_REGISTER,
    NotificationType.OFFICE_USER_REGISTER,
];
export const shopNotificationTypes = [
    NotificationType.NEW_PRODUCT_ASSIGNMENT,
    NotificationType.PRODUCT_ASSIGNMENT_7_DAY_WARNING,
];
export const handoverNotificationTypes = [
    NotificationType.PATIENT_HAND_OVER,
    NotificationType.CALL_THERAPIST_HANDOVER_ACTIVE,
    NotificationType.CALL_THERAPIST_HANDOVER_PASSIVE_THERAPIST,
    NotificationType.PATIENT_HAND_OVER_AS_PATIENT,
    NotificationType.CALL_THERAPIST_HANDOVER_PASSIVE_PATIENT,
];
/**
 * Appointment participation types that need the appointment header loaded at
 * enrichment time — invite / accept / decline on the therapist side. Used ONLY
 * by the notification enrichment layer to decide which notifications must read
 * the `Appointment` header (keyed off `appointmentId`); it is NOT a settings
 * category.
 *
 * The patient-facing `*_SUCCESS` confirmations are intentionally absent: they
 * are no longer dispatched (TM-8965), so there is nothing to enrich.
 */
export const appointmentParticipationNotificationTypes = [
    NotificationType.APPOINTMENT_INVITE,
    NotificationType.APPOINTMENT_ACCEPTED,
    NotificationType.APPOINTMENT_DECLINED,
    NotificationType.APPOINTMENT_CANCELLED_BY_PATIENT,
    NotificationType.BOOKING_CONFIRMED,
    NotificationType.BOOKING_DECLINED,
    NotificationType.BOOKING_AUTO_CANCELLED,
];
/**
 * All appointment-related notifications — the single "Termine" category for the
 * user's notification settings toggle. Deliberately makes NO distinction
 * between video calls and in-person appointments: a `Call` is just the remote
 * extension of an `Appointment`, so both ride the same toggle.
 *
 * The patient's own `APPOINTMENT_ACCEPT_SUCCESS` / `APPOINTMENT_DECLINE_SUCCESS`
 * confirmations are deliberately EXCLUDED (TM-8965): they are "own uncritical
 * actions" and carry no settings toggle, no email and no push.
 */
export const appointmentNotificationTypes = [
    NotificationType.APPOINTMENT_INVITE,
    NotificationType.APPOINTMENT_ACCEPTED,
    NotificationType.APPOINTMENT_DECLINED,
    NotificationType.APPOINTMENT_CANCELLED_BY_PATIENT,
    NotificationType.CALL_THERAPIST_HANDOVER_ACTIVE,
    NotificationType.CALL_THERAPIST_HANDOVER_PASSIVE_THERAPIST,
    NotificationType.CALL_BEGINNING_REMINDER,
    NotificationType.CALL_CANCELED,
    NotificationType.APPOINTMENT_RESCHEDULED,
    NotificationType.BOOKING_CONFIRMED,
    NotificationType.BOOKING_DECLINED,
    NotificationType.BOOKING_AUTO_CANCELLED,
];
/**
 * The "Übungsprogramme" settings category: everything about the programme
 * itself — it was created, changed, or ran out.
 *
 * The daily "today's exercises" digest is deliberately NOT here: it is a
 * recurring clock-driven nudge rather than news about a programme, so patients
 * mute it (or move it) independently via
 * {@link trainingReminderNotificationTypes} (TM-9530).
 */
export const exerciseProgramNotificationTypes = [
    NotificationType.PROGRAM_EXPIRED,
    NotificationType.NEW_EXERCISE_PROGRAM,
    NotificationType.EXERCISE_PROGRAM_UPDATED,
    NotificationType.EXERCISE_PROGRAM_COMPLETION_REMINDER,
];
/**
 * The "Trainingserinnerungen" settings category (TM-9530): the daily reminder
 * and nothing else. Split out of {@link exerciseProgramNotificationTypes} so a
 * patient can silence the daily nudge without also losing the notice that their
 * therapist changed their programme — and so the reminder can carry a send time
 * of its own (`UserSettings.trainingReminderTime`).
 */
export const trainingReminderNotificationTypes = [NotificationType.TODAYS_EXERCISE_SCHEDULE];
export const programStatsNotificationTypes = [
    NotificationType.NEW_PROGRAM_STATS,
    NotificationType.CRITICAL_PROGRAM_VALUES,
];
export const importantSystemNotificationForFrontend = [
    NotificationType.SYSTEM_USER_SESSIONS_ENDED,
    NotificationType.SYSTEM_USER_STRIPE_ENTITLEMENTS_UPDATED,
    NotificationType.SYSTEM_USER_STRIPE_PAYOUT_FAILED,
];
//# sourceMappingURL=notificationType.enum.js.map